import { useState } from "react";
import axios from "axios";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = ["A partnership", "A supplier enquiry", "Press or media", "Something else"];

const inputClass =
  "w-full bg-transparent border-0 border-b border-[#1A1C23]/20 focus:border-[#987E54] focus:ring-0 focus:outline-none px-0 py-3 text-base text-[#1A1C23] placeholder:text-[#757882]/70 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [state, setState] = useState({ status: "idle", error: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "sending", error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setState({ status: "sent", error: "" });
    } catch (err) {
      setState({
        status: "error",
        error: "Something went wrong sending that. Please email us directly instead.",
      });
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Antelli"
        description="Get in touch with Antelli. Partnerships, suppliers and press enquiries are welcome."
      />
      <div className="max-w-5xl mx-auto px-6 sm:px-10 pt-24 pb-24 sm:pt-36 sm:pb-36 grid grid-cols-1 md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#7A6034]">Contact</p>
            <h1
              data-testid="contact-heading"
              className="mt-6 font-serif-display text-4xl sm:text-5xl leading-tight text-[#1A1C23] font-medium"
            >
              A quiet word is welcome
            </h1>
            <p className="mt-8 text-base leading-relaxed text-[#4A4D57] max-w-md">
              We are glad to hear from partners, suppliers, affiliates and
              journalists — and from anyone doing their due diligence on the
              group. If it concerns one of the five brands specifically, say
              so and it will find the right desk.
            </p>
            <p className="mt-10 text-sm text-[#757882]">Or write directly:</p>
            <a
              href="mailto:support@antellihq.com"
              data-testid="contact-email-link"
              className="mt-2 inline-block text-base text-[#7A6034] border-b border-[#B69766]/50 hover:border-[#7A6034] pb-0.5 transition-colors duration-300"
            >
              support@antellihq.com
            </a>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            {state.status === "sent" ? (
              <div
                data-testid="contact-success-message"
                className="border border-[#1A1C23]/10 bg-white/60 p-10 sm:p-14"
              >
                <p className="font-serif-display text-2xl text-[#1A1C23]">
                  Thank you — it's on its way.
                </p>
                <p className="mt-4 text-base leading-relaxed text-[#4A4D57]">
                  Your message has been received and sent on to us. We read
                  everything ourselves and reply in person, usually within a
                  couple of days.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                  <div>
                    <label htmlFor="name" className="block text-xs uppercase tracking-[0.2em] text-[#757882] mb-1">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={onChange}
                      placeholder="Your name"
                      data-testid="contact-name-input"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-[0.2em] text-[#757882] mb-1">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-xs uppercase tracking-[0.2em] text-[#757882] mb-1">
                    What it's about
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    data-testid="contact-subject-select"
                    className={`${inputClass} cursor-pointer appearance-none`}
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs uppercase tracking-[0.2em] text-[#757882] mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    placeholder="A few lines is plenty."
                    data-testid="contact-message-input"
                    className={`${inputClass} resize-y`}
                  />
                </div>
                {state.status === "error" && (
                  <p data-testid="contact-error-message" className="text-sm text-[#8C4A3C]">
                    {state.error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={state.status === "sending"}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-3 border border-[#1A1C23] px-8 py-3.5 text-sm tracking-wide text-[#1A1C23] transition-all duration-300 hover:bg-[#1A1C23] hover:text-[#F4F3EF] disabled:opacity-50 disabled:cursor-wait"
                >
                  {state.status === "sending" ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </div>
  );
}
